import { supabase } from "@/integrations/supabase/client";
import type { User } from "@supabase/supabase-js";

/**
 * Resolve the current user reliably.
 *
 * `getUser()` validates against the auth server, so a stale/expired access
 * token makes it return null even though `getSession()` still reports
 * "signed in". Try an explicit refresh before giving up.
 *
 * Concurrent callers share one in-flight promise, and refresh only runs when a
 * local session exists — parallel refreshes fight over the auth lock and abort
 * with "Lock broken by another request with the 'steal' option".
 */
let inflight: Promise<User | null> | null = null;

const isLockError = (e: unknown) => /lock/i.test(String((e as Error)?.message ?? e));

async function resolveOnce(): Promise<User | null> {
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      const { data, error } = await supabase.auth.getUser();
      if (data?.user && !error) return data.user;
      if (error && isLockError(error)) throw error;
      break;
    } catch (e) {
      if (!isLockError(e) || attempt === 1) break;
      await new Promise((r) => setTimeout(r, 300));
    }
  }

  try {
    const { data: s } = await supabase.auth.getSession();
    if (!s.session) return null;
    const { data: refreshed } = await supabase.auth.refreshSession();
    if (refreshed?.user) return refreshed.user;
    const { data: retry } = await supabase.auth.getUser();
    return retry?.user ?? null;
  } catch {
    return null;
  }
}

export function resolveUser(): Promise<User | null> {
  if (!inflight) inflight = resolveOnce().finally(() => { inflight = null; });
  return inflight;
}
